import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import pluralize from "pluralize";
import { map, sort } from "ramda";
import { type CSSProperties, forwardRef, type ForwardRefRenderFunction, type ReactNode, useMemo } from "react";
import { mergeProps, type PressEvent, useButton, useHover } from "react-aria";

import { useDOMRef } from "../../hooks/useDomRef.ts";
import { sortAlpha } from "../../utils/string/sortAlpha.ts";
import { Checkbox } from "../Checkbox/Checkbox.tsx";
import { ColoredBullet } from "../ColoredLabel/ColoredLabel.tsx";
import { TruncateText } from "../FitTruncate/TruncateText.tsx";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import type { CollectionItem } from "../List/ListBox.tsx";
import type { Project } from "../Project/project.d.ts";
import { ProjectBadge } from "../Project/ProjectBadge.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { TagIntent } from "../Tag/settings.ts";
import { Tag } from "../Tag/Tag.tsx";
import { TimeAgo } from "../TimeAgo/TimeAgo.tsx";
import style from "./ItemTile.module.css";

interface TagIcon {
  id: string;
  icon: MaterialSymbol;
  children: ReactNode;
}
interface TagColor {
  id: string;
  color: CSSProperties["backgroundColor"];
  children: ReactNode;
}

export interface ItemTileProps extends CollectionItem {
  icon?: ReactNode;
  hint?: ReactNode;
  itemCount?: number | null;
  id: string | number;
  name: string | null;
  private?: boolean;
  description?: string | null;
  action?: ReactNode;
  typeLabel?: string | null;
  project?: Project | null;
  dateInfoTag?: string;
  userInfoTag?: string | null;
  infoTags?: (TagIcon | TagColor)[];
  itemTags?: { name: string; color: string | null }[];
  onPress?: (e: PressEvent) => void;
  onItemSelectionChange?: ((id: ItemTileProps["id"], selected: boolean) => void) | undefined;
  style?: CSSProperties;
}

const ItemTileRender: ForwardRefRenderFunction<HTMLDivElement, ItemTileProps> = (props, forwardedRef) => {
  const ref = useDOMRef(forwardedRef);
  const { hoverProps, isHovered } = useHover({ isDisabled: !props.onPress });
  const { buttonProps } = useButton({ onPress: props.onPress, elementType: "div", isDisabled: !props.onPress }, ref);

  const tags = useMemo(() => {
    if (props.itemTags) {
      return sort((a, b) => sortAlpha(a.name, b.name), props.itemTags);
    }

    return null;
  }, [props.itemTags]);

  return (
    <HStack
      domProps={props.onPress ? mergeProps(hoverProps, buttonProps) : undefined}
      className={clsx(style.item, {
        [style.selected]: props.isSelected,
        [style.hovered]: isHovered || props.isFocused,
        [style.disabled]: props.isDisabled,
      })}
      ref={ref}
      style={props.style}
      data-testid="tile"
    >
      {!!props.onItemSelectionChange && (
        <Checkbox
          value={"selected"}
          isSelected={props.isSelected}
          onChange={(isSelected) => props.onItemSelectionChange?.(props.id, isSelected)}
        />
      )}
      {!!props.icon && <div>{props.icon}</div>}
      <VStack style={{ overflow: "hidden", width: "100%" }}>
        <VStack spacing={1}>
          <HStack>
            <HStack spacing={10} style={{ overflow: "hidden" }}>
              <div data-testid={"name"} className={style.title}>
                {props.name && <TruncateText text={props.name} />}
              </div>
              {props.itemCount != null && (
                <Tag intentColor={TagIntent.Outlined} data-testid={"count"}>
                  {props.itemCount} {pluralize(props.typeLabel ?? "item", props.itemCount)}
                </Tag>
              )}
              {props.hint}
            </HStack>
            {props.private ? <SymbolIcon icon="lock" data-testid={"private"} /> : null}
          </HStack>
          <HStack spacing={10} className={style.info_tags}>
            {!!props.userInfoTag && (
              <HStack spacing={1} key="owner">
                <SymbolIcon icon="person" size={13} />
                {props.userInfoTag}
              </HStack>
            )}
            {!!props.dateInfoTag && (
              <HStack spacing={1} key="created">
                <SymbolIcon icon="calendar_today" size={13} />
                <TimeAgo dateTimeISO={props.dateInfoTag} />
              </HStack>
            )}
            {map(
              (tag) => (
                <HStack spacing={2} key={tag.id}>
                  {!!(tag as TagIcon).icon && <SymbolIcon icon={(tag as TagIcon).icon} size={13} />}
                  {!!(tag as TagColor).color && <ColoredBullet color={(tag as TagColor).color} />}
                  {tag.children}
                </HStack>
              ),
              props.infoTags ?? [],
            )}
          </HStack>
        </VStack>
        {!!props.description && (
          <div data-testid={"description"} className={style.description}>
            {props.description}
          </div>
        )}
      </VStack>
      {(!!tags || !!props.project || !!props.action) && (
        <>
          <Spacer />
          <HStack spacing={10} className={style.project_edit}>
            {tags?.map((tag) => (
              <Tag key={tag.name} intentColor={tag.color ?? "var(--disabled-border)"} label={tag.name} />
            ))}
            {!!props.project && <ProjectBadge project={props.project} size="normal" />}
            {props.action}
          </HStack>
        </>
      )}
    </HStack>
  );
};

export const ItemTile = forwardRef(ItemTileRender);
